import {createTheme, responsiveFontSizes, type ThemeOptions} from '@mui/material/styles';
// Side-effect-only import: registers MuiDateTimePickerToolbar (and the other
// @mui/x-date-pickers component slots) on MUI's ThemeOptions['components']
// type via module augmentation, so the styleOverrides below type-check.
// @ts-ignore
import type from '@mui/x-date-pickers/themeAugmentation';
import {renderTimeViewClock} from '@mui/x-date-pickers/timeViewRenderers';

export type ThemeMode = 'light' | 'dark';

/**
 * "Concept B — Field Ledger" theme.
 * Light: warm off-white page background, white rounded cards, burnt-orange
 * accent. Dark: matching accent tuned for contrast on dark surfaces.
 * Status colors match the mockup (green=normal/reported, amber=pending/
 * declining, red=overdue/critical) in both modes.
 */
const baseOptions: ThemeOptions = {
   typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h5: {fontWeight: 700},
      h6: {fontWeight: 700},
      subtitle2: {fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', fontSize: '0.7rem'},
   },
   shape: {
      borderRadius: 14,
   },
   components: {
      MuiAppBar: {
         styleOverrides: {
            root: ({theme}) => ({
               backgroundColor: theme.palette.background.paper,
               color: theme.palette.text.primary,
               boxShadow: 'none',
               borderBottom: `1px solid ${theme.palette.divider}`,
            }),
         },
      },
      MuiPaper: {
         styleOverrides: {
            root: {backgroundImage: 'none'},
         },
      },
      MuiButton: {
         defaultProps: {disableElevation: true},
         styleOverrides: {
            root: {borderRadius: 10, textTransform: 'none', fontWeight: 600},
         },
      },
      MuiChip: {
         styleOverrides: {
            root: {fontWeight: 600},
         },
      },
      // The mobile DateTimePicker toolbar (RunTicketFormPanel's Pickup/
      // Delivery date-time fields) renders "HH" and "MM" each inside a
      // PickersToolbarButton -> MUI Button, which is display:inline-flex
      // with align-items:center, so the digits get vertically centered.
      // The ":" separator between them is a bare PickersToolbarText span
      // with no such wrapper — as a flex item of the (align-items:
      // stretch by default) row it stretches to the row's height but its
      // text sits at the top of that box instead of centered, so the
      // colon renders visibly above/off-center from "04"/"35" (reported
      // by Duane 2026-09-04, screenshot showed it floating up-left of
      // center). Centering the separator's own box the same way the
      // buttons center theirs fixes it for every DateTimePicker in the
      // app, not just the ones in RunTicketFormPanel.tsx today.
      MuiDateTimePickerToolbar: {
         styleOverrides: {
            separator: {
               display: 'flex',
               alignItems: 'center',
            },
         },
      },
      // Duane wants the analog clock face (TimeClock — click/drag hour
      // and minute hands) for time selection, not MUI's default digital
      // scrollable-list UI (MultiSectionDigitalClock/DigitalClock), on
      // both DateTimePicker and TimePicker regardless of whether they're
      // rendering as the Desktop* (inline dropdown) or Mobile* (modal)
      // variant. DesktopDateTimePicker/DesktopTimePicker/
      // MobileDateTimePicker all default their hours/minutes/seconds
      // view renderers to renderMultiSectionDigitalClockTimeView (the
      // list UI); only MobileTimePicker defaults to renderTimeViewClock.
      // Overriding viewRenderers here forces the analog clock everywhere.
      MuiDateTimePicker: {
         defaultProps: {
            viewRenderers: {
               hours: renderTimeViewClock,
               minutes: renderTimeViewClock,
               seconds: renderTimeViewClock,
            },
         },
      },
      MuiTimePicker: {
         defaultProps: {
            viewRenderers: {
               hours: renderTimeViewClock,
               minutes: renderTimeViewClock,
               seconds: renderTimeViewClock,
            },
         },
      },
   },
};

const lightPalette = {
   mode: 'light' as const,
   primary: {dark: '#D74106', main: '#f05a1f', light: '#FF7439', contrastText: '#FFFFFF'},
   secondary: {main: '#1F2937', contrastText: '#FFFFFF'},
   success: {main: '#1E9E56'},
   warning: {main: '#D98E04'},
   error: {main: '#DC3B3B'},
   background: {default: '#F1EFEA', paper: '#FFFFFF'},
   text: {primary: '#1F2937', secondary: '#6B7280'},
   divider: 'rgba(31,31,31,0.2)',
};

const darkPalette = {
   mode: 'dark' as const,
   primary: {main: '#F0793F', contrastText: '#1A1004'},
   secondary: {main: '#F2F4F8', contrastText: '#111827'},
   success: {main: '#3FBE7C'},
   warning: {main: '#E5A83A'},
   error: {main: '#F0605F'},
   background: {default: '#15171B', paper: '#383B40'},
   text: {primary: '#E9E9EA', secondary: '#9CA3AF'},
   divider: 'rgba(255,255,255,0.2)',
};

export function getTheme(mode: ThemeMode = 'light') {
   const palette = mode === 'dark' ? darkPalette : lightPalette;

   return responsiveFontSizes(
      createTheme({
         ...baseOptions,
         palette,
      }),
      {factor: 4},
   );
}

// Kept for anywhere still importing the static light theme directly.
export const theme = getTheme('light');
